import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { incentivesApi, staffApi, branchApi, formatApiErrorDetail, useAuth } from '../../lib/api';
import { formatCurrency } from '../../lib/utils';
import { AppLayout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Award, Loader2, Users, Target, DollarSign, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { format, subMonths } from 'date-fns';

export default function IncentivesPage() {
  const { user } = useAuth();
  const location = useLocation();
  const isShopOwner = user?.role === 'SHOP_OWNER';
  const isManager = user?.role === 'STORE_MANAGER';
  const isStaffOnly = user?.role === 'SALES_STAFF';
  
  // Manager at /manager/incentives sees own earnings; at /manager/staff sees staff performance
  const showMyEarnings = isStaffOnly || (isManager && location.pathname.includes('/incentives'));
  const [incentives, setIncentives] = useState([]);
  const [myIncentives, setMyIncentives] = useState([]);
  const [branches, setBranches] = useState([]);
  const [commissionData, setCommissionData] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(format(new Date(), 'yyyy-MM'));
  const [branchFilter, setBranchFilter] = useState('all');

  const months = Array.from({ length: 6 }, (_, i) => {
    const date = subMonths(new Date(), i);
    return { value: format(date, 'yyyy-MM'), label: format(date, 'MMMM yyyy') };
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        if (showMyEarnings) {
          const { data } = await staffApi.getMyIncentives();
          setMyIncentives(data);
        } else {
          const params = { month: selectedMonth };
          if (branchFilter !== 'all') params.branch_id = branchFilter;
          const [incRes, brRes] = await Promise.all([
            incentivesApi.list(params), branchApi.list(),
          ]);
          setIncentives(incRes.data);
          setBranches(brRes.data.items || brRes.data);
          
          // Fetch commission for each staff
          const commissions = {};
          for (const inc of incRes.data) {
            try {
              const { data } = await staffApi.getCommissionSummary(inc.user_id, selectedMonth);
              commissions[inc.user_id] = data.total_commission;
            } catch { commissions[inc.user_id] = 0; }
          }
          setCommissionData(commissions);
        }
      } catch (error) {
        toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to load incentives');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedMonth, branchFilter, showMyEarnings]);

  const handleMarkPaid = async (staffId) => {
    try {
      await staffApi.markIncentivePaid(staffId, selectedMonth);
      toast.success('Incentive marked as paid');
      // Refresh
      const params = { month: selectedMonth };
      if (branchFilter !== 'all') params.branch_id = branchFilter;
      const { data } = await incentivesApi.list(params);
      setIncentives(data);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to update');
    }
  };

  const getStatusBadge = (percent) => {
    if (percent >= 100) return <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border">On Track</Badge>;
    if (percent >= 70) return <Badge className="bg-amber-50 text-amber-700 border-amber-200 border">At Risk</Badge>;
    return <Badge className="bg-red-50 text-red-700 border-red-200 border">Below Target</Badge>;
  };

  // Filter out shop owner's own entry from incentives list (tenant is not eligible for incentive payment)
  // Also filter out manager's own entry from their staff performance view
  let displayIncentives = incentives;
  if (isShopOwner) displayIncentives = incentives.filter(i => i.user_id !== user?.id);
  if (isManager) displayIncentives = incentives.filter(i => i.user_id !== user?.id);
  
  const totalStaff = displayIncentives.length;
  const onTrack = displayIncentives.filter(i => i.achievement_percent >= 100).length;
  const totalCommission = Object.values(commissionData).reduce((a, b) => a + b, 0);

  // Staff / Manager "My Earnings" View
  if (showMyEarnings) {
    return (
      <AppLayout>
        <div className="space-y-6">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>My Earnings</h1>
            <p className="text-slate-600 mt-1">Your incentive and commission history</p>
          </div>
          {loading ? (
            <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-[#005DE8]" /></div>
          ) : myIncentives.length > 0 ? (
            <div className="space-y-4">
              {myIncentives.map(inc => (
                <Card key={inc.month} className="border-slate-200">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-slate-900">{inc.month}</h3>
                      <div className="flex items-center gap-2">
                        {inc.commission > 0 ? (
                          <span className="text-lg font-bold text-[#005DE8]">{formatCurrency(inc.commission)}</span>
                        ) : (
                          <span className="text-sm text-slate-400">Commission tiers not configured</span>
                        )}
                        {inc.commission > 0 && (inc.paid ? (
                          <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border"><CheckCircle className="h-3 w-3 mr-1" />Paid</Badge>
                        ) : inc.paid_amount > 0 ? (
                          <Badge className="bg-amber-50 text-amber-700 border-amber-200 border">Partial ({formatCurrency(inc.unpaid_amount)} unpaid)</Badge>
                        ) : (
                          <Badge className="bg-amber-50 text-amber-700 border-amber-200 border">Unpaid</Badge>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-slate-600">
                      <span>Target: {inc.target}</span>
                      <span>Achieved: <strong>{inc.achieved}</strong></span>
                      <span>{inc.target > 0 ? Math.round(inc.achieved / inc.target * 100) : 0}%</span>
                    </div>
                    <Progress value={inc.target > 0 ? Math.min(inc.achieved / inc.target * 100, 100) : 0} className="h-2 mt-2" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="border-slate-200"><CardContent className="py-16 text-center">
              <Award className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900">No incentive data yet</h3>
              <p className="text-slate-500 mt-1">Start making warranty sales to earn commissions</p>
            </CardContent></Card>
          )}
        </div>
      </AppLayout>
    );
  }

  // Shop Owner / Manager View
  return (
    <AppLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>{isManager ? 'Staff Performance' : 'Staff Incentives'}</h1>
          <p className="text-slate-600 mt-1">{isManager ? 'Monitor your team\'s warranty sales performance' : 'Track warranty sales performance and commissions'}</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-slate-200"><CardContent className="pt-6">
            <div className="flex items-center gap-3"><div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center"><Users className="h-5 w-5 text-[#005DE8]" /></div>
            <div><p className="text-2xl font-bold text-slate-900">{totalStaff}</p><p className="text-sm text-slate-500">Total Staff</p></div></div>
          </CardContent></Card>
          <Card className="border-slate-200"><CardContent className="pt-6">
            <div className="flex items-center gap-3"><div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center"><Award className="h-5 w-5 text-emerald-600" /></div>
            <div><p className="text-2xl font-bold text-emerald-600">{onTrack}</p><p className="text-sm text-slate-500">On Track</p></div></div>
          </CardContent></Card>
          <Card className="border-slate-200"><CardContent className="pt-6">
            <div className="flex items-center gap-3"><div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center"><DollarSign className="h-5 w-5 text-purple-600" /></div>
            <div><p className="text-2xl font-bold text-purple-600">{formatCurrency(totalCommission)}</p><p className="text-sm text-slate-500">Total Commissions</p></div></div>
          </CardContent></Card>
          <Card className="border-slate-200"><CardContent className="pt-6">
            <div className="flex items-center gap-3"><div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center"><Target className="h-5 w-5 text-amber-600" /></div>
            <div><p className="text-2xl font-bold text-amber-600">{incentives.length - onTrack}</p><p className="text-sm text-slate-500">Needs Improvement</p></div></div>
          </CardContent></Card>
        </div>

        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                <SelectTrigger className="w-full sm:w-48" data-testid="month-filter"><SelectValue placeholder="Month" /></SelectTrigger>
                <SelectContent>{months.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
              </Select>
              {!isManager && (
                <Select value={branchFilter} onValueChange={setBranchFilter}>
                  <SelectTrigger className="w-full sm:w-48" data-testid="branch-filter"><SelectValue placeholder="Branch" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Branches</SelectItem>
                    {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.branch_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader><CardTitle style={{ fontFamily: 'Outfit' }}>Performance for {months.find(m => m.value === selectedMonth)?.label}</CardTitle></CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-[#005DE8]" /></div>
            ) : displayIncentives.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-100">
                    <TableHead>Staff</TableHead>
                    {!isManager && <TableHead>Branch</TableHead>}
                    <TableHead className="text-right">Target</TableHead>
                    <TableHead className="text-right">Achieved</TableHead>
                    <TableHead className="text-right">Commission</TableHead>
                    <TableHead>Progress</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Paid</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {displayIncentives.map(inc => (
                    <TableRow key={inc.id || inc.user_id} className="table-row-hover">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-[#005DE8] flex items-center justify-center text-white font-medium text-sm">{inc.staff_name?.charAt(0)?.toUpperCase() || 'S'}</div>
                          <span className="font-medium text-slate-900">{inc.staff_name}</span>
                        </div>
                      </TableCell>
                      {!isManager && <TableCell className="text-slate-600">{inc.branch_name || 'N/A'}</TableCell>}
                      <TableCell className="text-right">{inc.target_units}</TableCell>
                      <TableCell className="text-right font-semibold">{inc.achieved_units}</TableCell>
                      <TableCell className="text-right font-semibold text-[#005DE8]">
                        {inc.computed_commission !== undefined 
                          ? (inc.computed_commission > 0 ? formatCurrency(inc.computed_commission) : <span className="text-slate-400 text-xs font-normal">No tiers set</span>)
                          : commissionData[inc.user_id] !== undefined 
                            ? (commissionData[inc.user_id] > 0 ? formatCurrency(commissionData[inc.user_id]) : <span className="text-slate-400 text-xs font-normal">No tiers set</span>)
                            : <span className="text-slate-400 text-xs font-normal">-</span>
                        }
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 min-w-[100px]">
                          <Progress value={Math.min(inc.achievement_percent, 100)} className="h-2 flex-1" />
                          <span className="text-sm font-medium text-slate-600 w-12 text-right">{inc.achievement_percent}%</span>
                        </div>
                      </TableCell>
                      <TableCell>{getStatusBadge(inc.achievement_percent)}</TableCell>
                      <TableCell className="text-right">
                        {inc.paid ? (
                          <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border"><CheckCircle className="h-3 w-3 mr-1" />Paid</Badge>
                        ) : isShopOwner && inc.user_id !== user?.id ? (
                          <div className="flex flex-col items-end gap-1">
                            {inc.unpaid_amount > 0 && inc.paid_amount > 0 && (
                              <span className="text-xs text-amber-600">{formatCurrency(inc.unpaid_amount)} unpaid</span>
                            )}
                            <Button size="sm" variant="outline" onClick={() => handleMarkPaid(inc.user_id)} className="h-7 text-xs" data-testid={`mark-paid-${inc.user_id}`}>Mark Paid</Button>
                          </div>
                        ) : (
                          <Badge className="bg-amber-50 text-amber-700 border-amber-200 border">Unpaid</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-16">
                <Award className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900">No data available</h3>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
